export const formattedUSD = (value: number) =>
  value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: 'compact',
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  });

export const formattedDecimal = (value: number, digits?: number) => {
  if (digits === undefined) {
    digits = value < 1 ? 2 : 1;
  }
  return value.toLocaleString('en-US', {
    style: 'decimal',
    maximumFractionDigits: digits,
  });
};

export const formattedNumber = (value: number) =>
  Math.round((value + Number.EPSILON) * 100) / 100;

export const formattedPercent = (value: number) =>
  value.toLocaleString('en-US', {
    style: 'percent',
    notation: 'compact',
    maximumFractionDigits: value < 0.01 ? 2 : 1,
  });

export function rgbToHsl(rgb: { r: number; g: number; b: number }): {
  h: number;
  s: number;
  l: number;
} {
  const { r: r255, g: g255, b: b255 } = rgb;

  const r = r255 / 255;
  const g = g255 / 255;
  const b = b255 / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);

  let h = (max + min) / 2;
  let s = h;
  const l = h;

  if (max === min) {
    // Achromatic
    return { h: 0, s: 0, l };
  }

  const d = max - min;
  s = l >= 0.5 ? d / (2 - (max + min)) : d / (max + min);
  switch (max) {
    case r:
      h = ((g - b) / d + 0) * 60;
      break;
    case g:
      h = ((b - r) / d + 2) * 60;
      break;
    case b:
      h = ((r - g) / d + 4) * 60;
      break;
  }

  return { h: Math.round(h), s: Math.round(s * 100), l: Math.round(l * 100) };
}

export const SQUARE_FEET_PER_SQUARE_METER = 10.7639104167;
export const SQUARE_FEET_PER_ACRE = 43560;
export const SQUARE_FEET_PER_HECTARE = 107639.1;

/**
 * Safe wrapper around formattedUSD that handles null/undefined/NaN values.
 * Returns em-dash fallback and logs a console warning for data integrity.
 */
export function safeFormatUSD(
  value: number | string | undefined | null,
  context?: string,
): string {
  const num = Number(value);
  if (value === undefined || value === null || isNaN(num) || !isFinite(num)) {
    if (context) {
      console.warn(
        `[DataIntegrity] Invalid USD value in ${context}: ${JSON.stringify(value)}`,
      );
    }
    return '\u2014'; // em dash
  }
  return formattedUSD(num);
}

/**
 * Safe wrapper around formattedDecimal that handles null/undefined/NaN values.
 * Returns em-dash fallback and logs a console warning for data integrity.
 */
export function safeFormatDecimal(
  value: number | string | undefined | null,
  context?: string,
  digits?: number,
): string {
  const num = Number(value);
  if (value === undefined || value === null || isNaN(num) || !isFinite(num)) {
    if (context) {
      console.warn(
        `[DataIntegrity] Invalid decimal value in ${context}: ${JSON.stringify(value)}`,
      );
    }
    return '\u2014'; // em dash
  }
  return formattedDecimal(num, digits);
}

/**
 * Safe wrapper around formattedPercent that handles null/undefined/NaN values.
 * Returns em-dash fallback and logs a console warning for data integrity.
 */
export function safeFormatPercent(
  value: number | string | undefined | null,
  context?: string,
): string {
  const num = Number(value);
  if (value === undefined || value === null || isNaN(num) || !isFinite(num)) {
    if (context) {
      console.warn(
        `[DataIntegrity] Invalid percent value in ${context}: ${JSON.stringify(value)}`,
      );
    }
    return '\u2014'; // em dash
  }
  return formattedPercent(num);
}
